/**
 * Display-time date/time formatting.
 *
 * The server stores + returns timestamps as UTC (ISO with `Z`). ALL
 * timezone interpretation happens HERE, client-side, in the user's chosen
 * display zone (see useTimezoneStore / useDateFormat). Rule: never send a
 * locally-formatted wall-clock back to the server — only UTC instants.
 */

export type DateInput = string | number | Date | null | undefined;

export const EMPTY_DATE = '—';

function toDate(value: DateInput): Date | null {
  if (value == null) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Intl throws if `dateStyle`/`timeStyle` are combined with granular fields
 * (`year`, `hour`, …). When a caller opts into either style, drop our
 * granular defaults so the two never collide.
 */
function mergeOpts(
  defaults: Intl.DateTimeFormatOptions,
  opts: Intl.DateTimeFormatOptions | undefined,
  timeZone: string,
): Intl.DateTimeFormatOptions {
  if (opts && (opts.dateStyle != null || opts.timeStyle != null)) {
    return { timeZone, ...opts };
  }
  return { timeZone, ...defaults, ...opts };
}

export function formatDateTime(
  value: DateInput,
  timeZone: string,
  opts?: Intl.DateTimeFormatOptions,
): string {
  const d = toDate(value);
  if (!d) return EMPTY_DATE;
  return new Intl.DateTimeFormat(
    undefined,
    mergeOpts(
      {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      },
      opts,
      timeZone,
    ),
  ).format(d);
}

/**
 * Format a *calendar date* — a day with no time-of-day and no timezone
 * (date of birth, an appointment's day, an order's due date). Backed by a
 * Postgres `date` column, these are NOT instants: there's no "moment" to
 * convert, so applying a display zone could shift them across midnight and
 * show the wrong day. We pin formatting to UTC so the exact `YYYY-MM-DD` the
 * server sent is the day shown — identically for every viewer, in any zone.
 *
 * Use this ONLY for `date`-column values. For `timestamp` instants (createdAt,
 * signedAt, visitDate, …) use formatDate/formatTime/formatDateTime, which
 * convert UTC → the chosen display zone.
 */
export function formatCalendarDate(
  value: DateInput,
  opts?: Intl.DateTimeFormatOptions,
): string {
  const d = toDate(value);
  if (!d) return EMPTY_DATE;
  return new Intl.DateTimeFormat(
    undefined,
    mergeOpts({ year: 'numeric', month: 'short', day: 'numeric' }, opts, 'UTC'),
  ).format(d);
}

export function formatDate(
  value: DateInput,
  timeZone: string,
  opts?: Intl.DateTimeFormatOptions,
): string {
  const d = toDate(value);
  if (!d) return EMPTY_DATE;
  return new Intl.DateTimeFormat(
    undefined,
    mergeOpts(
      { year: 'numeric', month: 'short', day: 'numeric' },
      opts,
      timeZone,
    ),
  ).format(d);
}

export function formatTime(
  value: DateInput,
  timeZone: string,
  opts?: Intl.DateTimeFormatOptions,
): string {
  const d = toDate(value);
  if (!d) return EMPTY_DATE;
  return new Intl.DateTimeFormat(
    undefined,
    mergeOpts({ hour: 'numeric', minute: '2-digit' }, opts, timeZone),
  ).format(d);
}

/**
 * Relative time ("3 minutes ago"). It's a delta between two instants, so
 * it's timezone-independent — no zone needed. Falls back to an absolute,
 * zoned date for anything older than ~30 days.
 */
export function formatRelative(value: DateInput, timeZone: string): string {
  const d = toDate(value);
  if (!d) return EMPTY_DATE;
  const diffMs = d.getTime() - Date.now();
  const abs = Math.abs(diffMs);
  const MIN = 60_000;
  const HOUR = 3_600_000;
  const DAY = 86_400_000;
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  if (abs < MIN) return rtf.format(-Math.round(0), 'second') || 'now';
  if (abs < HOUR) return rtf.format(Math.round(diffMs / MIN), 'minute');
  if (abs < DAY) return rtf.format(Math.round(diffMs / HOUR), 'hour');
  if (abs < 30 * DAY) return rtf.format(Math.round(diffMs / DAY), 'day');
  return formatDate(d, timeZone);
}

/** Short label for a zone, e.g. "Asia/Dubai · GMT+4". */
export function timeZoneLabel(timeZone: string): string {
  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZone,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date());
    const offset = parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
    return offset ? `${timeZone} · ${offset}` : timeZone;
  } catch {
    return timeZone;
  }
}

/** All IANA zones the runtime knows, for a picker. Falls back to a curated list. */
export function supportedTimeZones(): string[] {
  const intl = Intl as unknown as {
    supportedValuesOf?: (key: string) => string[];
  };
  if (typeof intl.supportedValuesOf === 'function') {
    try {
      return intl.supportedValuesOf('timeZone');
    } catch {
      /* fall through */
    }
  }
  return [
    'UTC',
    'Africa/Cairo',
    'Asia/Riyadh',
    'Asia/Dubai',
    'Asia/Qatar',
    'Asia/Kuwait',
    'Asia/Bahrain',
    'Europe/London',
    'Europe/Paris',
    'America/New_York',
    'America/Los_Angeles',
  ];
}
